import React, { useState, useMemo } from 'react';
import { User, Users, Plus, Eye, Edit, Trash2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import type { AccountExecutive, AccountExecutiveMaintenanceState, AccountExecutivePaginationInfo } from '../../../../types/accountExecutiveMaintenance';
import { mockAccountExecutives } from '../../../../data/mockAccountExecutives';
import AccountExecutiveDetailsModal from './components/AccountExecutiveDetailsModal';
import '../../../../styles/accountExecutiveMaintenance.css';

interface AccountExecutiveMaintenanceProps {
  className?: string;
  onEditAccountExecutive?: (accountExecutive: AccountExecutive) => void;
}

const AccountExecutiveMaintenance: React.FC<AccountExecutiveMaintenanceProps> = ({ className = '', onEditAccountExecutive }) => {
  const [state, setState] = useState<AccountExecutiveMaintenanceState>({
    accountExecutives: mockAccountExecutives,
    searchTerm: '',
    currentPage: 1,
    itemsPerPage: 5,
    totalAccountExecutives: mockAccountExecutives.length,
    selectedAccountExecutives: [],
    isLoading: false
  });

  const [viewDetailsModal, setViewDetailsModal] = useState<{
    isOpen: boolean;
    accountExecutive: AccountExecutive | null;
  }>({ isOpen: false, accountExecutive: null });

  // Filter account executives based on search term
  const filteredAccountExecutives = useMemo(() => {
    if (!state.searchTerm) {
      return state.accountExecutives;
    }

    const searchLower = state.searchTerm.toLowerCase();
    return state.accountExecutives.filter(ae =>
      ae.aeCode.toLowerCase().includes(searchLower) ||
      ae.firstName.toLowerCase().includes(searchLower) ||
      ae.middleName.toLowerCase().includes(searchLower) ||
      ae.lastName.toLowerCase().includes(searchLower)
    );
  }, [state.accountExecutives, state.searchTerm]);

  // Calculate pagination
  const paginationInfo = useMemo((): AccountExecutivePaginationInfo => {
    const totalAccountExecutives = filteredAccountExecutives.length;
    const totalPages = Math.ceil(totalAccountExecutives / state.itemsPerPage);
    const startAccountExecutive = (state.currentPage - 1) * state.itemsPerPage + 1;
    const endAccountExecutive = Math.min(state.currentPage * state.itemsPerPage, totalAccountExecutives);

    return {
      currentPage: state.currentPage,
      totalPages,
      totalAccountExecutives,
      itemsPerPage: state.itemsPerPage,
      startAccountExecutive: totalAccountExecutives > 0 ? startAccountExecutive : 0,
      endAccountExecutive: totalAccountExecutives > 0 ? endAccountExecutive : 0
    };
  }, [filteredAccountExecutives.length, state.currentPage, state.itemsPerPage]);

  // Get current page account executives
  const currentPageAccountExecutives = useMemo(() => {
    const startIndex = (state.currentPage - 1) * state.itemsPerPage;
    const endIndex = startIndex + state.itemsPerPage;
    return filteredAccountExecutives.slice(startIndex, endIndex);
  }, [filteredAccountExecutives, state.currentPage, state.itemsPerPage]);

  const handleSearchChange = (searchTerm: string) => {
    setState(prev => ({
      ...prev,
      searchTerm,
      currentPage: 1 // Reset to first page when searching
    }));
  };

  const handlePageChange = (page: number) => {
    setState(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const handleCreateNew = () => {
    alert('Create New Account Executive Modal\n\nThis would open a form with:\n- AE Code input\n- Name details input\n- Status configuration\n- Account activation options');
  };

  const handleViewAccountExecutive = (accountExecutive: AccountExecutive) => {
    setViewDetailsModal({ isOpen: true, accountExecutive });
  };

  const handleEditAccountExecutive = (accountExecutive: AccountExecutive) => {
    if (onEditAccountExecutive) {
      onEditAccountExecutive(accountExecutive);
    } else {
      alert(`Edit Account Executive Modal\n\nThis would open an edit form for:\n- AE Code: ${accountExecutive.aeCode}\n- Name: ${accountExecutive.firstName} ${accountExecutive.middleName} ${accountExecutive.lastName}\n- Status modifications`);
    }
  };

  const handleDeleteAccountExecutive = (accountExecutive: AccountExecutive) => {
    const confirmDelete = confirm(`Are you sure you want to delete account executive "${accountExecutive.firstName} ${accountExecutive.lastName}" (${accountExecutive.aeCode})?\n\nThis action cannot be undone.`);
    if (confirmDelete) {
      setState(prev => ({
        ...prev,
        accountExecutives: prev.accountExecutives.filter(ae => ae.id !== accountExecutive.id),
        totalAccountExecutives: prev.totalAccountExecutives - 1
      }));
      alert(`Account Executive "${accountExecutive.firstName} ${accountExecutive.lastName}" has been deleted successfully.`);
    }
  };

  const renderPagination = () => {
    return (
      <div className="account-executive-pagination">
        <div className="pagination-info">
          Showing {paginationInfo.startAccountExecutive} to {paginationInfo.endAccountExecutive} of {paginationInfo.totalAccountExecutives} account executives
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(paginationInfo.currentPage - 1)}
            disabled={paginationInfo.currentPage === 1}
          >
            <ChevronLeft size={16} />
          </button>
          
          {Array.from({ length: paginationInfo.totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`pagination-number ${page === paginationInfo.currentPage ? 'active' : ''}`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}
          
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(paginationInfo.currentPage + 1)}
            disabled={paginationInfo.currentPage === paginationInfo.totalPages}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`account-executive-maintenance ${className}`}>
      {/* Header */}
      <div className="account-executive-maintenance-header">
        <div className="account-executive-maintenance-header-left">
          <div className="account-executive-maintenance-icon">
            <User size={24} />
          </div>
          <div className="account-executive-maintenance-title-section">
            <h1 className="account-executive-maintenance-title">Manage Account Executive</h1>
            <div className="account-executive-maintenance-context">
              Manage and oversee account executive records and activities
            </div>
          </div>
        </div>
        
        <div className="account-executive-maintenance-header-actions">
          <button
            className="btn-create-new-account-executive"
            onClick={handleCreateNew}
          >
            <Plus size={16} />
            Create New
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="account-executive-maintenance-search-section">
        <div className="account-executive-search-controls">
          <div className="account-executive-search-input-container">
            <Search size={16} className="account-executive-search-icon" />
            <input
              type="text"
              placeholder="Search by AE code, first name, or last name..."
              value={state.searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="account-executive-search-input"
            />
            <div className="account-executive-search-tooltip">
              Search by AE code, first name, or last name. Type to filter results instantly.
            </div>
          </div>
        </div>
        
        <div className="account-executive-count-info">
          <Users size={16} className="account-executive-count-icon" />
          <span className="account-executive-count-text">{filteredAccountExecutives.length} account executives found</span>
        </div>
      </div>

      {/* Table */}
      <div className="account-executive-table-container">
        <table className="account-executive-table">
          <thead className="account-executive-table-header">
            <tr>
              <th className="ae-code-column">AE CODE</th>
              <th className="first-name-column">FIRST NAME</th>
              <th className="middle-name-column">MIDDLE NAME</th>
              <th className="last-name-column">LAST NAME</th>
              <th className="is-active-column">IS ACTIVE</th>
              <th className="actions-column">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="account-executive-table-body">
            {currentPageAccountExecutives.map((accountExecutive) => (
              <tr key={accountExecutive.id} className="account-executive-row">
                <td className="ae-code-cell">{accountExecutive.aeCode}</td>
                <td className="first-name-cell">{accountExecutive.firstName}</td>
                <td className="middle-name-cell">{accountExecutive.middleName}</td>
                <td className="last-name-cell">{accountExecutive.lastName}</td>
                <td className="is-active-cell">
                  <div className={`status-indicator ${accountExecutive.isActive ? 'active' : 'inactive'}`}>
                    <div className={`status-dot ${accountExecutive.isActive ? 'active' : 'inactive'}`}></div>
                    {accountExecutive.isActive ? 'Active' : 'Inactive'}
                  </div>
                </td>
                <td>
                  <div className="account-executive-action-buttons">
                    <button
                      className="account-executive-action-btn view"
                      onClick={() => handleViewAccountExecutive(accountExecutive)}
                      title="View Account Executive"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      className="account-executive-action-btn edit"
                      onClick={() => handleEditAccountExecutive(accountExecutive)}
                      title="Edit Account Executive"
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      className="account-executive-action-btn delete"
                      onClick={() => handleDeleteAccountExecutive(accountExecutive)}
                      title="Delete Account Executive"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {renderPagination()}

      {/* Account Executive Details Modal */}
      <AccountExecutiveDetailsModal
        isOpen={viewDetailsModal.isOpen}
        onClose={() => setViewDetailsModal({ isOpen: false, accountExecutive: null })}
        accountExecutive={viewDetailsModal.accountExecutive}
        onEdit={(accountExecutive: AccountExecutive) => {
          setViewDetailsModal({ isOpen: false, accountExecutive: null });
          handleEditAccountExecutive(accountExecutive);
        }}
      />
    </div>
  );
};

export default AccountExecutiveMaintenance;